import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api, experimentApi, billingApi } from '../utils/api';
import { useAuth } from './AuthProvider';

const StoreContext = createContext(null);

const CACHE_KEY = 'jambofy_store_cache';
// Bump this when the cached data shape changes (e.g., new fields from API)
const CACHE_VERSION = 2;

// In-memory cache - survives component remounts within the same page load.
// Fallback for when sessionStorage is blocked in third-party iframes (Safari ITP, etc).
let _memoryCache = null;

// Pure read - never mutates storage. Returns the cached payload only when the
// shop matches; mismatches are reported as a miss. Cache wiping is the caller's
// responsibility (done explicitly in the load effect on a real shop switch).
function getCache(shop) {
  if (!shop) return null;
  if (_memoryCache?.v === CACHE_VERSION && _memoryCache.shop === shop) {
    return _memoryCache;
  }
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.v === CACHE_VERSION && parsed.shop === shop) return parsed;
    return null;
  } catch { return null; }
}

function setCache(shop, data) {
  if (!shop) return;
  const versioned = { ...data, shop, v: CACHE_VERSION };
  _memoryCache = versioned;
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(versioned)); } catch {}
}

function clearCache() {
  _memoryCache = null;
  try { sessionStorage.removeItem(CACHE_KEY); } catch {}
}

// Exported for tests - wipes the module-level in-memory cache so that each
// test renders with a clean slate. Not used at runtime.
export function __resetStoreCacheForTests() {
  _memoryCache = null;
  try { sessionStorage.removeItem(CACHE_KEY); } catch {}
}

/**
 * StoreProvider - Manages store data and bars state.
 *
 * Cache validation runs in a background effect, never during render. The
 * dashboard renders exactly once when state is fully resolved - AppLayout's
 * skeleton covers the brief window between mount and the effect's commit.
 */
export function StoreProvider({ children }) {
  const { isAuthenticated, isLoading: authLoading, authError, shop } = useAuth();

  const [store, setStore] = useState(null);
  const [billing, setBilling] = useState(null);
  const [bars, setBars] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [experiments, setExperiments] = useState([]);
  const [plans, setPlans] = useState([]);

  // Fetch store data. Patches the sessionStorage cache so subsequent reloads
  // (e.g. the billing-callback redirect landing back on /app/billing) hydrate
  // with fresh plan state instead of the pre-upgrade snapshot.
  const fetchStore = useCallback(async () => {
    try {
      const response = await api.get('/api/store');
      setStore(response.store);
      setBilling(response.billing);
      if (shop) {
        const cached = getCache(shop);
        if (cached) {
          setCache(shop, {
            ...cached,
            store: response.store,
            billing: response.billing,
          });
        }
      }
    } catch (err) {
      console.error('Failed to fetch store:', err);
      setError(err.message);
    }
  }, [shop]);

  // Fetch bars. Also patches the sessionStorage cache so a subsequent page
  // reload rehydrates with the just-fetched list instead of stale bars from
  // the bootstrap snapshot. All CRUD actions (create/update/delete/toggle/
  // duplicate) route through here, so the cache stays in sync on writes.
  const fetchBars = useCallback(async () => {
    try {
      const response = await api.get('/api/bars');
      const nextBars = response.bars || [];
      setBars(nextBars);
      if (shop) {
        const cached = getCache(shop);
        if (cached) setCache(shop, { ...cached, bars: nextBars });
      }
      return response;
    } catch (err) {
      console.error('Failed to fetch bars:', err);
      setError(err.message);
      return { bars: [] };
    }
  }, [shop]);

  // Create bar
  const createBar = useCallback(async (config) => {
    try {
      const response = await api.post('/api/bars', config);
      await fetchBars();
      return { success: true, bar: response };
    } catch (err) {
      return { success: false, error: err.response?.data || err.message };
    }
  }, [fetchBars]);

  // Update bar
  const updateBar = useCallback(async (barId, config) => {
    try {
      const response = await api.put(`/api/bars/${barId}`, config);
      await fetchBars();
      return { success: true, bar: response };
    } catch (err) {
      return { success: false, error: err.response?.data || err.message };
    }
  }, [fetchBars]);

  // Delete bar
  const deleteBar = useCallback(async (barId) => {
    try {
      await api.delete(`/api/bars/${barId}`);
      await fetchBars();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data || err.message };
    }
  }, [fetchBars]);

  // Toggle bar active
  const toggleBar = useCallback(async (barId) => {
    try {
      const response = await api.post(`/api/bars/${barId}/toggle`);
      await fetchBars();
      return { success: true, bar: response };
    } catch (err) {
      return { success: false, error: err.response?.data || err.message };
    }
  }, [fetchBars]);

  // Duplicate bar
  const duplicateBar = useCallback(async (barId) => {
    try {
      const response = await api.post(`/api/bars/${barId}/duplicate`);
      await fetchBars();
      return { success: true, bar: response };
    } catch (err) {
      return { success: false, error: err.response?.data || err.message };
    }
  }, [fetchBars]);

  // Get bar by ID
  const getBar = useCallback(async (barId) => {
    try {
      const response = await api.get(`/api/bars/${barId}`);
      return { success: true, bar: response };
    } catch (err) {
      return { success: false, error: err.response?.data || err.message };
    }
  }, []);

  // Create bar from template
  const createBarFromTemplate = useCallback(async (templateId, overrides = {}) => {
    try {
      const response = await api.post('/api/bars/from-template', {
        template_id: templateId,
        overrides,
      });
      await fetchBars();
      return { success: true, bar: response };
    } catch (err) {
      return { success: false, error: err.response?.data || err.message };
    }
  }, [fetchBars]);

  // Fetch experiments
  const fetchExperiments = useCallback(async () => {
    try {
      const response = await experimentApi.getAll();
      setExperiments(response.experiments || []);
    } catch (err) {
      console.error('Failed to fetch experiments:', err);
    }
  }, []);

  // Fetch billing plans
  const fetchPlans = useCallback(async () => {
    try {
      const data = await billingApi.getPlans();
      setPlans(data.plans || []);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchStore(), fetchBars(), fetchExperiments(), fetchPlans()]);
  }, [fetchStore, fetchBars, fetchExperiments, fetchPlans]);

  // Tracks the last non-null shop we've seen so we can distinguish between
  // "shop became known for the first time" (no reset needed) and "user
  // switched to a different shop" (reset + refetch).
  const lastShop = React.useRef(null);
  const fetchStarted = React.useRef(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setIsLoading(false); return; }
    if (!shop) return;

    const firstResolve = lastShop.current === null;
    const shopChanged = !firstResolve && lastShop.current !== shop;
    if (!firstResolve && !shopChanged) return;
    lastShop.current = shop;

    // Commit all five pieces of state plus isLoading=false in one go.
    // React 18 batches these into a single re-render - the dashboard mounts
    // exactly once with full data, no intermediate flicker.
    const commit = (payload) => {
      setStore(payload.store);
      setBilling(payload.billing);
      setBars(payload.bars);
      setExperiments(payload.experiments);
      setPlans(payload.plans);
      setIsLoading(false);
    };

    // Cache hit for the current shop - render immediately, no fetch.
    if (!shopChanged) {
      const cached = getCache(shop);
      if (cached) {
        commit({
          store: cached.store || null,
          billing: cached.billing || null,
          bars: cached.bars || [],
          experiments: cached.experiments || [],
          plans: cached.plans || [],
        });
        return;
      }
    } else {
      // Real store switch - drop the previous shop's cached data so nothing
      // from the prior merchant leaks while we fetch.
      clearCache();
      fetchStarted.current = false;
    }

    if (fetchStarted.current) return;
    fetchStarted.current = true;

    (async () => {
      // Force /auth/token to complete before the parallel fan-out so the
      // requests carry an Authorization header on their first attempt. On
      // a cold post-redirect mount the request interceptor's exchange
      // otherwise races against the four parallel calls and they fire
      // without auth (visible as a burst of 401s in the backend log).
      await api.ensureFreshToken();
      const [storeRes, barsRes, expsRes, plansRes] = await Promise.allSettled([
        api.get('/api/store'),
        api.get('/api/bars'),
        experimentApi.getAll(),
        billingApi.getPlans(),
      ]);

      const s = storeRes.status === 'fulfilled' ? storeRes.value : null;
      const b = barsRes.status === 'fulfilled' ? (barsRes.value.bars || []) : [];
      const e = expsRes.status === 'fulfilled' ? (expsRes.value.experiments || []) : [];
      const p = plansRes.status === 'fulfilled' ? (plansRes.value.plans || []) : [];

      const payload = {
        store: s?.store || null,
        billing: s?.billing || null,
        bars: b,
        experiments: e,
        plans: p,
      };
      setCache(shop, payload);
      commit(payload);
    })();
  }, [isAuthenticated, authLoading, shop]);

  const value = useMemo(() => ({
    store,
    billing,
    bars,
    isLoading,
    error,
    authLoading,
    authError,
    shop,
    isAuthenticated,
    isPremium: billing?.is_premium || false,
    isInTrial: billing?.is_in_trial || false,
    canCreateBar: bars.length < 1 || billing?.is_premium,
    experiments,
    plans,

    // Actions
    fetchStore,
    fetchBars,
    fetchExperiments,
    fetchPlans,
    createBar,
    updateBar,
    deleteBar,
    toggleBar,
    duplicateBar,
    getBar,
    createBarFromTemplate,
    refreshData,
    clearCache,
  }), [
    store, billing, bars, isLoading, error,
    authLoading, authError, shop, isAuthenticated, experiments, plans,
    fetchStore, fetchBars, fetchExperiments, fetchPlans, createBar, updateBar, deleteBar,
    toggleBar, duplicateBar, getBar, createBarFromTemplate, refreshData,
  ]);

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}

export function useStore() {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
}
